import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import { FlatListReadOnly } from '@openx/components-core/lib/TransferList/FlatListReadOnly';
import { ActionType, type EnrichedListOption } from '@openx/components-core/lib/TransferList/types';
import { isNonEmptyArr } from '@openx/components-core/lib/TransferList/utils/utils';
import type { LalSegmentApi, SeedListData } from '@openx/types/segments';
import { abbreviatedNumber } from '@openx/utils/lib/abbreviatedNumber';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SeedDetailsAlerts } from '../../components/SeedDetailsAlerts/SeedDetailsAlerts';
import { SeedRenderItem } from '../../LalSegmentsForm/components/SeedListsAdd/SeedRenderItem';

const ITEM_SIZE = 80;

export const LalSegmentSeedsSection = ({ lalSegment }: { lalSegment: LalSegmentApi }) => {
  const { t } = useTranslation();

  const segments: EnrichedListOption<SeedListData>[] | null = useMemo(() => {
    const sources = lalSegment.lookalike_seed_sources;

    if (!sources?.length) {
      return null;
    }

    return sources.map(({ segment }) => ({
      id: segment.id,
      name: segment.name,
      reach: segment.reach ?? 0,
      status: segment.status,
      expiration_date: segment.expiration_date,
      isOnList: false,
    }));
  }, [lalSegment.lookalike_seed_sources]);

  const customSearch = useCallback((data: SeedListData[], searchInput: string) => {
    const search = searchInput.toLowerCase();

    return data.filter(item => item.name.toLowerCase().includes(search) || item.id.toLowerCase().includes(search));
  }, []);

  return (
    <FormSection divider title={t('Seed Segments')} data-test="seeds-details">
      <FieldDetails title={t('Seed Name')} value={lalSegment.lookalike_seeds_aggregate_name} />
      <FieldDetails
        title={t('Estimated Unique Reach')}
        value={`${abbreviatedNumber(lalSegment.seeds_estimated_reach ?? 0)}`}
      />
      <Box>
        <Typography
          variant="body1"
          sx={{ mb: 1, fontWeight: 'fontWeightBold', fontSize: 15 }}
          data-test="segment-list-title"
        >
          {t('Seed Source Segments')}
        </Typography>
        <SeedDetailsAlerts seeds={segments} />
        {segments && isNonEmptyArr(segments) && (
          <Grid data-test="segment-list">
            <FlatListReadOnly
              data={segments}
              actionType={ActionType.Block}
              customItem={SeedRenderItem}
              itemSize={ITEM_SIZE}
              customSearch={customSearch}
              containerStyles={{ minWidth: 555 }}
              customPlaceholder={t('Search Name or ID')}
            />
          </Grid>
        )}
      </Box>
    </FormSection>
  );
};
